import React, { useEffect, useState } from 'react';
import { CreditForm } from '../Forms/CreditForm';
import UserDetailModal from '../Forms/UserDetailModal';
import CustomerDetailsView from '../Forms/CustomerDetailsView';
import UserPaymentDetails from '../Tables/UserPaymentDetails';
import UsersTable from '../customertable';
import { Toast } from '../../../components/Common/Toast';
import PaginationButtons from '../../../components/Common/Pagination';
import { NoRecordFound } from '../../../components/Common/NoRecordFound';
import Dialogs from '../../../components/Common/Dialog';
import { BookModel } from '../../../interfaces/book.interface';
import { UserModel, UserShortModel } from '../../../interfaces/user.interface';
import {
  deleteUser,
  generateImpersonationToken,
  getActiveUsers,
  getAllUsers,
  getTotalUsers,
  searchUsers,
  unsubscribeUserPlan,
  updateUserData,
  updateUserStatus,
} from './services/AdminCustomers.service';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import CustomerPlanSelector from './components/CustomerPlanSelector';
import { setAllUserPlansList } from '../../../store/appSlice';
import TableInfoBar from '../../../components/Admin/TableInfoBar';
import { AdminNavHandler } from '../adminNav';

interface CustomerProps {
  onOptionClick: AdminNavHandler;
  onSelectUser: (...args: any) => void;
  initialSearchText?: string;
}

export interface Plan {
  id: string | number;
  name: string;
}

export interface AllUserPlans {
  totalUsers: number;
  selectedPlanActiveUsers: number;
  plans: Plan[];
}

const Customer = ({ onOptionClick, onSelectUser, initialSearchText }: CustomerProps) => {
  const user = useSelector((state: RootState) => state.user.user);
  const dispatch: AppDispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [allUsers, setAllUsers] = useState<UserModel[]>([]);
  const [email, setEmail] = useState<string | null | undefined>(null);
  const [showCreditForm, setShowCreditForm] = useState<any | null>(null);
  const [creditDetails, setCreditDetails] = useState<number | null | any>(null);
  const [selectedUserInfo, setSelectedUserInfo] = useState<UserModel | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState(initialSearchText || '');
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isUserDetailModalOpen, setisUserDetailModalOpen] = useState(false);
  const [paymentDetailsModalOpen, setPaymentDetailsModalOpen] = useState(false);
  const [customerDetailsViewOpen, setCustomerDetailsViewOpen] = useState(false);
  const [searchLoader, setSearchLoader] = useState(false);
  const [selectedBook, setSelectedBook] = useState<BookModel | null>(null);
  const [allUserPlans, setAllUserPlans] = useState<AllUserPlans>();
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('signup');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchAllUsers = async (search?: string, searchMode = false) => {
    const setL = searchMode ? setSearchLoader : setLoader;
    try {
      setL(true);
      const data = await getAllUsers(currentPage, sortBy, sortOrder, roleFilter || undefined, search || undefined, statusFilter || undefined);
      setAllUsers(data.users);
      setTotalPages(data.totalPages);
      setTotalRecords(data.totalRecords);
    } finally {
      setL(false);
    }
  };

  // Helper: does this user match the current search text?
  const matchesSearch = (u: UserModel, text: string): boolean => {
    const t = text.toLowerCase();
    return (
      u.firstName?.toLowerCase().includes(t) ||
      u.lastName?.toLowerCase().includes(t) ||
      u.email?.toLowerCase().includes(t) ||
      u.lead?.leadId?.toLowerCase().includes(t) ||
      false
    );
  };

  const handleSearchUsers = async (term?: string) => {
    const query = term || searchText;
    try {
      setSearchLoader(true);
      const users = await searchUsers(query);
      // If server returned matches (name/email search), use them.
      // Otherwise keep the current page so the client-side leadId filter can still work.
      if (users.message?.length > 0) {
        setAllUsers(users.message);
      }
    } finally {
      setSearchLoader(false);
    }
  };

  const handleFetchTotalUsers = async () => {
    try {
      setSearchLoader(true);
      const users = await getTotalUsers();
      setAllUserPlans(users);
      dispatch(setAllUserPlansList(users?.plans || []));
    } finally {
      setSearchLoader(false);
    }
  };

  const handleFetchActiveUsers = async (selectedPlanId: string) => {
    try {
      setSearchLoader(true);
      if (!selectedPlanId) {
        fetchAllUsers();
        return;
      }
      const users = await getActiveUsers(selectedPlanId);
      setAllUserPlans(users);
      setAllUsers(users.users);
    } finally {
      setSearchLoader(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser(email!);
      await fetchAllUsers();
      Toast('User Deleted Successfully');
      setOpenDialog(false);
    } catch (error) {
      Toast('Failed to delete user', true);
    }
  };

  const handleUpdateUserStatus = async (email: string, newStatus: string) => {
    try {
      const success = await updateUserStatus(email, newStatus);
    } finally {
    }
  };

  useEffect(() => {
    if (showCreditForm === false) {
      setCreditDetails(null);
      setSelectedBook(null);
    }
  }, [showCreditForm]);

  useEffect(() => {
    handleFetchTotalUsers();
  }, [currentPage]);

  useEffect(() => {
    fetchAllUsers(searchText || undefined);
  }, [currentPage, sortBy, sortOrder, roleFilter, statusFilter]);

  const handlePageChange = (page: number | any) => {
    if (page > 0 && page <= totalPages!) setCurrentPage(+page);
  };

  const handleStatusChange = async (email: string | undefined, newStatus: string) => {
    if (!email) return;
    try {
      await handleUpdateUserStatus(email, newStatus);
      const updatedUsers = allUsers.map((user: UserModel) => {
        if (user.email === email) {
          return { ...user, status: newStatus };
        }
        return user;
      });
      setAllUsers(updatedUsers);
      fetchAllUsers();
    } catch (error) {}
  };

  const handleEditCredits = (userId: string | undefined) => {
    if (!userId) return;
    if (selectedBook) {
      setCreditDetails(
        (+(selectedBook?.creditsPerBookLimit ?? 0) - +(selectedBook?.booksCreatedCounter ?? 0)).toString(),
      );
    } else {
      const userInfo: any = allUsers.filter((val) => val.id === userId);
      setCreditDetails(parseInt(userInfo[0]?.booksCreateLimit || 0) - parseInt(userInfo[0]?.booksCreatedCounter || 0));
    }
    setShowCreditForm(userId);
  };

  const closeUserDetailModal = () => {
    setisUserDetailModalOpen(false);
  };

  const closePaymentDetailsModal = () => {
    setPaymentDetailsModalOpen(false);
  };

  const closeCustomerDetailsView = () => {
    setCustomerDetailsViewOpen(false);
  };

  const handleSearchInputChange = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    if (value && value.length >= 2) {
      fetchAllUsers(value, true);
    } else if (!value) {
      fetchAllUsers(undefined, true);
    }
  };

  useEffect(() => {
    handleEditCredits(selectedUserInfo?.id!);
  }, [selectedBook]);

  const handleUnsubscribeFromPlan = async (email: string) => {
    await unsubscribeUserPlan(email);
    await fetchAllUsers();
  };

  const handleUpdateUserData = async (email: string, property: keyof UserModel, value: string) => {
    await updateUserData(email, { [property]: value });
    await fetchAllUsers();
  };

  const handleImpersonateUser = async (userToImpersonate: UserModel) => {
    try {
      if (!userToImpersonate.id) {
        Toast('Unable to impersonate user: User ID not found', true);
        return;
      }

      const response = await generateImpersonationToken(userToImpersonate.id);
      const next = encodeURIComponent(response.redirectPath || '/');
      const accessToken = encodeURIComponent(response.accessToken);
      const impersonationUrl = `${window.location.origin}/#/admin/impersonate#accessToken=${accessToken}&next=${next}`;

      window.open(impersonationUrl, '_blank', 'noopener,noreferrer');
      Toast(`Impersonating ${userToImpersonate.email} in new tab`, false);
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      Toast('Failed to impersonate user. Please try again.', true);
    }
  };

  return (
    <div className="p-0 md:p-4">
      {loader ? (
        <LoadingSpinner />
      ) : showCreditForm ? (
        <CreditForm
          showCreditForm={showCreditForm}
          setShowCreditForm={setShowCreditForm}
          getAllUsers={fetchAllUsers}
          getUserBooks={async () => {}}
          creditDetails={creditDetails!}
          book={selectedBook}
        />
      ) : (
        <>
          {!isUserDetailModalOpen && !paymentDetailsModalOpen && !customerDetailsViewOpen && (
            <>
              <CustomerPlanSelector
                allUserPlans={allUserPlans!}
                handleFetchActiveUsers={handleFetchActiveUsers}
                handleSearchInputChange={handleSearchInputChange}
                roleFilter={roleFilter}
                handleRoleFilterChange={(role) => { setRoleFilter(role); setCurrentPage(1); }}
                statusFilter={statusFilter}
                handleStatusFilterChange={(status) => { setStatusFilter(status); setCurrentPage(1); }}
                initialSearchText={initialSearchText}
              />
              {searchLoader ? (
                <LoadingSpinner />
              ) : (
                <>
                  <UsersTable
                    allUsers={allUsers}
                    showUserRole={true}
                    showDemoAccess={true}
                    showLastSessionData={true}
                    largeTable={true}
                    onOptionClick={onOptionClick}
                    onSelectUser={onSelectUser}
                    handleStatusChange={handleStatusChange}
                    loggedinUser={user}
                    handleEditCredits={handleEditCredits}
                    setSelectedUserInfo={setSelectedUserInfo}
                    setisUserDetailModalOpen={setisUserDetailModalOpen}
                    setOpenDialog={setOpenDialog}
                    setEmail={setEmail}
                    NoRecordFound={NoRecordFound}
                    sortOrder={sortOrder}
                    setSortOrder={setSortOrder}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    unsubscribeFromPlan={handleUnsubscribeFromPlan}
                    updateUserData={handleUpdateUserData}
                    handleImpersonateUser={handleImpersonateUser}
                    onViewCustomerDetails={(user) => {
                      setSelectedUserInfo(user);
                      setCustomerDetailsViewOpen(true);
                    }}
                  />
                  <div className="flex justify-between items-center mt-4">
                    <TableInfoBar
                      currentPage={currentPage}
                      pageSize={25}
                      totalPages={totalPages || 0}
                      totalRecords={totalRecords}
                      recordsOnPage={allUsers.length}
                    />
                    {totalPages! > 1 && (
                      <PaginationButtons
                        totalPages={totalPages!}
                        currentPage={currentPage}
                        handlePageChange={handlePageChange}
                      />
                    )}
                  </div>
                </>
              )}
            </>
          )}
          {isUserDetailModalOpen && (
            <UserDetailModal
              user={selectedUserInfo as UserModel}
              isUserDetailModalOpen={isUserDetailModalOpen}
              onClose={closeUserDetailModal}
              refresh={() => setTimeout(() => fetchAllUsers(), 500)}
            />
          )}
          {paymentDetailsModalOpen && (
            <UserPaymentDetails
              user={selectedUserInfo as UserShortModel}
              paymentDetailsModalOpen={paymentDetailsModalOpen}
              onClose={closePaymentDetailsModal}
            />
          )}
          {customerDetailsViewOpen && (
            <CustomerDetailsView
              user={selectedUserInfo as UserModel}
              isOpen={customerDetailsViewOpen}
              onClose={closeCustomerDetailsView}
              onOptionClick={onOptionClick}
            />
          )}
        </>
      )}
      <Dialogs openDialog={openDialog} handelClose={() => setOpenDialog(false)} handelProcess={handleDeleteUser} />
    </div>
  );
};
export default Customer;
